'use client';

import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  CalendarIcon,
  ShoppingCart,
  Monitor,
  MapPin,
  Globe,
  Clock,
  Smartphone,
  Laptop,
  Mail,
  User,
  Tag as TagIcon,
  Ellipsis,
  FlaskConical,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Customer } from '@/lib/api/customers';
import { DateDisplay } from '@/components/date-display';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { OrderResourceBadge } from '@/components/orders/OrderResourceBadge';
import { useStoreFilterOptions } from '@/lib/api/stores';
import {
  useBrowserFilterOptions,
  useOperatingSystemFilterOptions,
  useDeviceTypeFilterOptions,
  useDeviceModelFilterOptions,
  useCountryFilterOptions,
  useTagsFilterOptions,
} from '@/lib/api/devices';
import { Badge } from '@/components/ui/badge';
import { CustomerTagsBadge } from '@/components/customer/CustomerTagsBadge';


interface GetCustomersTableColumnsProps {
  t: (key: string) => string;
}

export function getCustomersTableColumns({ t }: GetCustomersTableColumnsProps): ColumnDef<Customer>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    // 2. Email (primary identifier)
    {
      id: 'email',
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.email')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const isDraft = (customer as any).draft === true;
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            {isDraft && (
              <Badge variant="yellow" className="h-4 gap-0.5 px-1 text-[9px] font-normal shrink-0">
                <FlaskConical className="h-2.5 w-2.5" />
                Draft
              </Badge>
            )}
            <div className="truncate text-sm font-medium">{customer.email || '-'}</div>
          </div>
        );
      },
      enableSorting: true,
      enableColumnFilter: false, // Only searchable via global search
      meta: { label: t('table.columns.email') },
    },
    {
      id: 'store',
      accessorKey: 'store.name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.store')} />,
      cell: ({ row }) => {
        const customer = row.original;
        return <div className="truncate">{customer.store?.name || '-'}</div>;
      },
      meta: {
        label: t('table.columns.store'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useStoreFilterOptions,
        icon: () => <ShoppingCart />,
      },
      enableColumnFilter: true,
    },
    {
      id: 'firstName',
      accessorKey: 'firstName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.firstName')} />,
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate font-medium">{customer.firstName || '-'}</div>
          </div>
        );
      },
      enableSorting: true,
      enableColumnFilter: false, // Only searchable via global search
      meta: { label: t('table.columns.firstName') },
    },
    {
      id: 'lastName',
      accessorKey: 'lastName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.lastName')} />,
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate font-medium">{customer.lastName || '-'}</div>
          </div>
        );
      },
      enableSorting: true,
      enableColumnFilter: false, // Only searchable via global search
      meta: { label: t('table.columns.lastName') },
    },
    {
      id: 'lastOrderId',
      accessorKey: 'lastOrderId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.lastOrderId')} />
      ),
      cell: ({ row }) => {
        const customer = row.original;
        return customer.lastOrderId ? (
          <OrderResourceBadge orderId={customer.lastOrderId} />
        ) : (
          <div className="min-w-0">
            <div className="text-muted-foreground truncate text-sm">-</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.lastOrderId'),
        placeholder: t('table.filters.searchPlaceholder'),
        variant: 'text',
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'browser',
      accessorKey: 'device.browser',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.browser')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.browser) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <Monitor className="text-muted-foreground h-4 w-4" />
            <div className="flex flex-col">
              <div className="text-sm">{device.browser}</div>
              {device.browserVersion && (
                <div className="text-muted-foreground text-xs">{device.browserVersion}</div>
              )}
            </div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.browser'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useBrowserFilterOptions,
        icon: () => <Monitor />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'operatingSystem',
      accessorKey: 'device.os',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.os')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.os) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <Laptop className="text-muted-foreground h-4 w-4" />
            <div className="flex flex-col">
              <div className="text-sm">{device.os}</div>
              {device.osVersion && <div className="text-muted-foreground text-xs">{device.osVersion}</div>}
            </div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.os'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useOperatingSystemFilterOptions,
        icon: () => <Laptop />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'deviceType',
      accessorKey: 'device.deviceType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.deviceType')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.deviceType) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <Smartphone className="text-muted-foreground h-4 w-4" />
            <Badge variant="secondary" className="capitalize">
              {device.deviceType}
            </Badge>
          </div>
        );
      },
      meta: {
        label: t('table.columns.deviceType'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useDeviceTypeFilterOptions,
        icon: () => <Smartphone />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'deviceModel',
      accessorKey: 'device.deviceModel',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.deviceModel')} />
      ),
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.deviceModel) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <Smartphone className="text-muted-foreground h-4 w-4" />
            <div className="text-sm">{device.deviceModel}</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.deviceModel'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useDeviceModelFilterOptions,
        icon: () => <Smartphone />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'ipAddress',
      accessorKey: 'device.geoIpAddress',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.ipAddress')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.geoIpAddress) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return <div className="font-mono text-sm">{device.geoIpAddress}</div>;
      },
      enableSorting: false,
      enableColumnFilter: false, // Only searchable via global search
    },
    {
      id: 'location',
      accessorKey: 'device.geoCountry',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.location')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.geoCountry) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <Globe className="text-muted-foreground h-4 w-4" />
            <div className="flex flex-col">
              <div className="text-sm">
                {device.geoCity && `${device.geoCity}, `}
                {device.geoCountry}
              </div>
              {device.geoCountryCode && (
                <Badge variant="outline" className="w-fit text-xs">
                  {device.geoCountryCode}
                </Badge>
              )}
            </div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.location'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useCountryFilterOptions,
        icon: () => <Globe />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'lastSeen',
      accessorKey: 'device.lastSeenAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.lastSeen')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const device = (customer as any).device;
        if (!device || !device.lastSeenAt) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        return (
          <DateDisplay value={device.lastSeenAt} format="MM/dd/yyyy HH:mm" className="text-sm tabular-nums" />
        );
      },
      meta: {
        label: t('table.columns.lastSeen'),
        variant: 'dateRange',
        icon: () => <Clock />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'tags',
      accessorKey: 'tags',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.tags')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const tags = (customer as any).tags || [];
        return <CustomerTagsBadge tags={tags} maxVisible={2} editable={false} />;
      },
      meta: {
        label: t('table.columns.tags'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useTagsFilterOptions,
        icon: () => <TagIcon />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.createdAt')} />,
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <DateDisplay
            value={customer.createdAt}
            format="MM/dd/yyyy HH:mm"
            className="text-sm tabular-nums"
            fallback="-"
          />
        );
      },
      meta: {
        label: t('table.columns.createdAt'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    // Customer ID (moved to end, copyable)
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.customerId')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const displayValue = `${customer.id.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.customerId')}
            value={displayValue}
            copyValue={customer.id}
          />
        );
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
      meta: { label: t('table.columns.customerId') },
    },
    // Actions column
    {
      id: 'actions',
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem>
                {t('table.actions.viewDetails')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
}
